import { forwardRef, type ComponentPropsWithoutRef } from "react";
import styles from "./SearchTrigger.module.scss";
import classNames from "classnames";

type SearchTriggerProps = ComponentPropsWithoutRef<"button"> & {
	label: string;
	/** Hint shown instead of the value while open with nothing selected yet. */
	placeholder?: string;
	value?: string;
	error?: boolean;
	isOpen?: boolean;
};

export const SearchTrigger = forwardRef<HTMLButtonElement, SearchTriggerProps>(
	({ label, placeholder, value, error, isOpen, className, ...rest }, ref) => {
		const isFloating = isOpen || !!value;
		const displayValue = value ?? (isOpen ? placeholder : undefined);

		const buttonClassName = classNames(styles["search-trigger"], className, {
			[styles["search-trigger--error"]]: error,
			[styles["search-trigger--open"]]: isOpen,
		});

		const labelClassName = classNames(styles["search-trigger__label"], {
			[styles["search-trigger__label--floating"]]: isFloating,
		});

		const valueClassName = classNames(styles["search-trigger__value"], {
			[styles["search-trigger__value--placeholder"]]: !value,
		});

		return (
			<button
				ref={ref}
				type="button"
				aria-haspopup="dialog"
				aria-expanded={isOpen}
				className={buttonClassName}
				{...rest}
			>
				<div className={styles["search-trigger__content"]}>
					<span className={labelClassName}>{label}</span>

					{displayValue && <span className={valueClassName}>{displayValue}</span>}
				</div>
			</button>
		);
	}
);

SearchTrigger.displayName = "SearchTrigger";
